import {
  PhoneOutline18Icon,
  PhoneOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { ButtonHTMLAttributes, FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "shared/components/Spor";
import { usePuzzelCurrentlyCalling } from "stores/usePuzzleCurrentlyCalling";

type Size = "xs" | "sm";

const getCallIcon = (size: Size) => {
  switch (size) {
    case "xs":
      return <PhoneOutline18Icon />;
    case "sm":
      return <PhoneOutline24Icon />;
  }
};

export const formatReadablePhoneNumber = (phoneNumber?: string | null) => {
  if (!phoneNumber) return "";

  let normalizedPhoneNumber = phoneNumber;
  if (phoneNumber.startsWith("00")) {
    normalizedPhoneNumber = `+${phoneNumber.slice(2)}`;
  } else if (/^\d{8}$/.test(phoneNumber)) {
    // Assume it's a Norwegian local number without country code
    normalizedPhoneNumber = `+47${phoneNumber}`;
  }

  const match = normalizedPhoneNumber.match(/^\+(\d{2})(\d{3})(\d{2})(\d{3})$/);
  if (!match) return normalizedPhoneNumber;

  const [, countryCode, firstGroup, secondGroup, finalGroup] = match;
  return `(+${countryCode}) ${firstGroup} ${secondGroup} ${finalGroup}`;
};

/*
    This is a workaround. If we send in the plus to Puzzel, it is stripped away
    before the 18740 prefix is added. This works fine if you're an admin, but
    not for normal DROPS users. But if we replace the + with 00, it is not stripped,
    and with the 00 it works just fine. Don't ask me why.
   
    These formats work for everyone:
    18740 0047 12345678 <- Result from what we send in with the workaround
    18740 12345678
    0047 12345678
    12345678

    This format only works for admins:
    18740 47 12345678 <- Result from what we send in without the workaround
  */
const cleanUpPhoneNumber = (phoneNumber: string) =>
  phoneNumber.replace("+", "00");

export interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  phoneNumber: string;
  label?: string;
  size: Size;
  insideButton?: boolean;
}

/**
 * Secondary button with tooltip on hover, for initiating calls via Puzzel.
 * @param id - ID for linking up the button and tooltip components
 * @param phoneNumber - Phone number to initiate call with.
 * @param label - Optional label to display instead of the phone number.
 * @param size - Size of the secondary Button; only possible to choose between x-small and small atm.
 */
export const CallButton: FC<Props> = ({
  phoneNumber,
  label,
  size,
  insideButton,
  ...props
}) => {
  const { setCurrentlyCalling } = usePuzzelCurrentlyCalling();
  const { t } = useTranslation();

  const safePhoneNumber = cleanUpPhoneNumber(phoneNumber);

  const readablePhoneNumber = formatReadablePhoneNumber(safePhoneNumber);

  const toolTipText = [t("Ring til"), readablePhoneNumber, label]
    .filter(Boolean)
    .join(" ");

  return (
    <Tooltip positioning={{ placement: "top" }}>
      <TooltipTrigger>
        <Button
          variant="tertiary"
          size={size}
          whiteSpace="nowrap"
          leftIcon={<div>{getCallIcon(size)}</div>}
          aria-label={toolTipText}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setCurrentlyCalling(safePhoneNumber);
          }}
          as={insideButton ? "span" : "button"}
          {...props}
        >
          {label ?? readablePhoneNumber}
        </Button>
      </TooltipTrigger>
      <TooltipContent showArrow>{toolTipText}</TooltipContent>
    </Tooltip>
  );
};
